import React, { useMemo } from 'react';
import { Box } from '@material-ui/core';
import CommentIcon from '@material-ui/icons/Comment';
import NoteTitle from './subcomponents/NoteTitle';
import ShowNoteContent from './subcomponents/ShowNoteContent';
import ShowNoteImages from './subcomponents/ShowNoteImages';
import ShowNoteAudios from './subcomponents/ShowNoteAudios';
import ShowNoteDocuments from './subcomponents/ShowNoteDocuments';
import ShowNoteSigns from './subcomponents/ShowNoteSigns';
import ShowNoteExternalIdentification from './subcomponents/ShowNoteExternalIdentification';
import ShowNoteForms from './subcomponents/ShowNoteForms';
import useGetMedias from './hooks/useGetMedias';

interface Props {
    note: any;
}

// GlobalNoteStandard aligned with platform-v2, adapted to MUI v4 and local subcomponents
const GlobalNoteStandard: React.FC<Props> = ({ note }) => {
    const time: string = useMemo(() => note?.createdAt ?? '', [note]);
    const authInfo: any = useMemo(() => note?.data?.auth, [note]);

    const content: string | undefined = useMemo(
        () => note?.data?.customFields?.forms?._note_?.content,
        [note]
    );

    // Medias via shared hook
    const mediasIds: string[] = useMemo(
        () => (Array.isArray(note?.data?.medias) ? note.data.medias.map((m: any) => m?.id).filter(Boolean) : []),
        [note]
    );
    const { images, audios, documents, isLoading: isLoadingMedias } = useGetMedias({ mediasIds });

    const signs: any[] = useMemo(() => note?.data?.signatures, [note]);
    const externalIdentification = useMemo(() => note?.data?.customFields?.external_identification, [note]);

    // Fix configuration version like in platform-v2 (inline helper adapted from Traceability)
    const GLOBAL_NOTE_TYPE = 'GlobalNote';
    const FORM_KEY_VERSION_1 = 'data';
    const FORM_KEY_VERSION_2 = '_note_';

    const fixTypeNoteConfigurationVersion2AsVersion1 = (typeNote: any, data: any) => {
        const newData = { ...data };
        if (typeNote && newData && String(typeNote.name).toLowerCase() === GLOBAL_NOTE_TYPE.toLowerCase()) {
            try {
                const componentForms = typeNote?.configuration?.customFields?.componentForms;
                if (
                    Array.isArray(componentForms) &&
                    componentForms.length > 0 &&
                    FORM_KEY_VERSION_1 in (componentForms[0] || {}) &&
                    FORM_KEY_VERSION_2 in newData
                ) {
                    newData[FORM_KEY_VERSION_1] = newData[FORM_KEY_VERSION_2];
                    return newData;
                }
            } catch (e) {
                // keep passthrough on structure mismatch
            }
        }
        return newData;
    };

    const formsData = useMemo(
        () => fixTypeNoteConfigurationVersion2AsVersion1(note?.typeNote, note?.data?.customFields?.forms),
        [note]
    );
    const formsOrigin = useMemo(() => note?.typeNote?.configuration?.customFields?.componentForms, [note]);

    const title = useMemo(() => formsData?._note_?.title ?? '---', [formsData]);

    return (
        <Box style={{ display: 'grid', gap: 8, background: 'white', paddingTop: 8, paddingBottom: 8 }}>
            <NoteTitle icon={<CommentIcon />} title={title} time={time} authInfo={authInfo} />

            {content && <ShowNoteContent content={content} />}

            {!isLoadingMedias && images && images.length > 0 && (
                <ShowNoteImages imagesUrls={images.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && audios && audios.length > 0 && (
                <ShowNoteAudios audios={audios.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && documents && documents.length > 0 && (
                <ShowNoteDocuments
                    documents={documents
                        .filter((d) => !!d.urlToDownload)
                        .map((d) => ({
                            urlToDownload: d.urlToDownload as string,
                            name: (d.name as string | undefined) || ((d.urlToDownload as string).split('/').pop() || 'document'),
                        }))}
                />
            )}

            {signs && signs.length > 0 && <ShowNoteSigns signs={signs} />}

            {externalIdentification && (
                <ShowNoteExternalIdentification externalIdentification={externalIdentification} />
            )}

            {formsData && formsOrigin && formsOrigin.length > 0 && (
                <ShowNoteForms formsData={formsData} formsOrigin={formsOrigin} typeNote={note?.typeNote} />
            )}
        </Box>
    );
};

export default GlobalNoteStandard;
